/**
 * GST State Code Map
 * Key   → 2-digit state code
 * Value → Full state name
 */
export const GST_STATE_CODES = {
    "01": "Jammu & Kashmir",
    "02": "Himachal Pradesh",
    "03": "Punjab",
    "04": "Chandigarh",
    "05": "Uttarakhand",
    "06": "Haryana",
    "07": "Delhi",
    "08": "Rajasthan",
    "09": "Uttar Pradesh",
    "10": "Bihar",
    "11": "Sikkim",
    "12": "Arunachal Pradesh",
    "13": "Nagaland",
    "14": "Manipur",
    "15": "Mizoram",
    "16": "Tripura",
    "17": "Meghalaya",
    "18": "Assam",
    "19": "West Bengal",
    "20": "Jharkhand",
    "21": "Odisha",
    "22": "Chhattisgarh",
    "23": "Madhya Pradesh",
    "24": "Gujarat",
    "25": "Daman & Diu",
    "26": "Dadra & Nagar Haveli and Daman & Diu",
    "27": "Maharashtra",
    "28": "Andhra Pradesh (Pre-bifurcation)",
    "29": "Karnataka",
    "30": "Goa",
    "31": "Lakshadweep",
    "32": "Kerala",
    "33": "Tamil Nadu",
    "34": "Puducherry",
    "35": "Andaman & Nicobar Islands",
    "36": "Telangana",
    "37": "Andhra Pradesh",
    "38": "Ladakh",
    "97": "Other Territory",
    "99": "Centre Jurisdiction",
  };
  
  /**
   * State abbreviation (ISO 3166-2:IN) → GST 2-digit code
   *
   * email_hotel_state stores abbreviations like "HR", "KA", "MH", "DL"
   * This map converts them to GST codes so they can be compared
   * directly against the first 2 digits of any GSTIN.
   */
  export const STATE_ABBR_TO_GST_CODE = {
    JK: "01",  // Jammu & Kashmir
    HP: "02",  // Himachal Pradesh
    PB: "03",  // Punjab
    CH: "04",  // Chandigarh
    UK: "05",  // Uttarakhand
    UT: "05",  // Uttarakhand (alternate)
    HR: "06",  // Haryana
    DL: "07",  // Delhi
    RJ: "08",  // Rajasthan
    UP: "09",  // Uttar Pradesh
    BR: "10",  // Bihar
    SK: "11",  // Sikkim
    AR: "12",  // Arunachal Pradesh
    NL: "13",  // Nagaland
    MN: "14",  // Manipur
    MZ: "15",  // Mizoram
    TR: "16",  // Tripura
    ML: "17",  // Meghalaya
    AS: "18",  // Assam
    WB: "19",  // West Bengal
    JH: "20",  // Jharkhand
    OD: "21",  // Odisha
    OR: "21",  // Odisha (alternate)
    CG: "22",  // Chhattisgarh
    CT: "22",  // Chhattisgarh (alternate)
    MP: "23",  // Madhya Pradesh
    GJ: "24",  // Gujarat
    DD: "25",  // Daman & Diu (legacy)
    DN: "26",  // Dadra & Nagar Haveli
    MH: "27",  // Maharashtra
    AP: "37",  // Andhra Pradesh
    KA: "29",  // Karnataka
    GA: "30",  // Goa
    LD: "31",  // Lakshadweep
    KL: "32",  // Kerala
    TN: "33",  // Tamil Nadu
    PY: "34",  // Puducherry
    AN: "35",  // Andaman & Nicobar Islands
    TG: "36",  // Telangana
    TS: "36",  // Telangana (alternate)
    LA: "38",  // Ladakh
  };
  
  /**
   * Convert a state abbreviation to its GST 2-digit code.
   * Case-insensitive. Returns null if not found.
   *
   * @param {string} abbr  e.g. "HR", "MH", "ka"
   * @returns {string|null}
   */
  export const getGstCodeFromAbbr = (abbr) => {
    if (!abbr || typeof abbr !== "string") return null;
    return STATE_ABBR_TO_GST_CODE[abbr.trim().toUpperCase()] ?? null;
  };
  
  /**
   * Extract the 2-digit state code from a GSTIN string.
   * Returns null if the input is missing or malformed.
   *
   * @param {string} gstin
   * @returns {string|null}
   */
  export const getStateCodeFromGstin = (gstin) => {
    if (!gstin || typeof gstin !== "string") return null;
    const code = gstin.trim().slice(0, 2);
    return /^\d{2}$/.test(code) ? code : null;
  };
  
  /**
   * Resolve a GSTIN to its full state name.
   * Returns null if unrecognised.
   */
  export const getStateFromGstin = (gstin) => {
    const code = getStateCodeFromGstin(gstin);
    return code ? (GST_STATE_CODES[code] ?? null) : null;
  };
  
  /**
   * Compare the state codes of two GSTINs.
   */
  export const doGstinStatesMatch = (gstinA, gstinB) => {
    const codeA = getStateCodeFromGstin(gstinA);
    const codeB = getStateCodeFromGstin(gstinB);
    if (!codeA || !codeB) return false;
    return codeA === codeB;
  };